import {useCallback, useEffect, useRef} from 'react';
import {debounce} from 'lodash';

type Task<TResult> = () => TResult;

export type DebouncedTask<TResult> = ((
	task: Task<TResult>,
) => TResult | undefined) & {
	cancel: () => void;
	flush: () => TResult | undefined;
};

type UseDebouncerResult<TResult> = {
	schedule: (task: Task<TResult>) => TResult | undefined;
	cancel: () => void;
	flush: () => TResult | undefined;
};

export const useDebouncer = <TResult>(
	wait: number,
): UseDebouncerResult<TResult> => {
	const debouncedTaskRef = useRef<DebouncedTask<TResult> | null>(null);

	const getDebouncedTask = useCallback((): DebouncedTask<TResult> => {
		const current = debouncedTaskRef.current;

		if (current !== null) {
			return current;
		}

		const debouncedTask = debounce((task: Task<TResult>) => task(), wait);

		debouncedTaskRef.current = debouncedTask;

		return debouncedTask;
	}, [wait]);

	const schedule = useCallback(
		(task: Task<TResult>): TResult | undefined => {
			return getDebouncedTask()(task);
		},
		[getDebouncedTask],
	);

	const cancel = useCallback(() => {
		debouncedTaskRef.current?.cancel();
	}, []);

	const flush = useCallback((): TResult | undefined => {
		const debouncedTask = debouncedTaskRef.current;

		if (debouncedTask === null) {
			return undefined;
		}

		return debouncedTask.flush();
	}, []);

	useEffect(() => {
		return () => {
			debouncedTaskRef.current?.cancel();
			debouncedTaskRef.current = null;
		};
	}, [wait]);

	return {
		schedule,
		cancel,
		flush,
	};
};

export default useDebouncer;
